import { useCallback, useEffect, useRef, useState } from 'react';
import { api, ApiError } from '../api/client';
import './DomainInventory.css';

/* ─── Types ──────────────────────────────────────────────── */

type RunStatus =
  | 'queued'
  | 'phase_a_running'
  | 'phase_a_done'
  | 'awaiting_confirmation'
  | 'cancelled'
  | 'phase_b1_running'
  | 'phase_b2_waiting'
  | 'phase_b3_running'
  | 'phase_b4_running'
  | 'phase_b5_verifying'
  | 'completed'
  | 'failed';

type ItemStatus =
  | 'pending'
  | 'not_found'
  | 'primary_skipped'
  | 'b1_users_killed'
  | 'b3_users_verified'
  | 'b4_domain_deleted'
  | 'b4_needs_group_auth'
  | 'b5_verified_deleted'
  | 'error';

interface RemovalRun {
  id: string;
  status: RunStatus;
  trigger_source?: 'manual' | 'auto' | 'rerun';
  triggered_by: string | null;
  sheet_url: string;
  sheet_id: string;
  inventory_run_id: string | null;
  rerun_of_run_id: string | null;
  domains_requested: number;
  domains_targeted: number;
  domains_not_found: number;
  domains_primary: number;
  users_deleted: number;
  groups_deleted: number;
  domains_deleted: number;
  domains_failed: number;
  domains_needs_group_auth: number;
  ghost_domains_deleted: number;
  ghost_domains_failed: number;
  error_message: string | null;
  created_at: string;
  started_at: string | null;
  confirmed_at: string | null;
  finished_at: string | null;
}

interface RemovalRunItem {
  id: string;
  run_id: string;
  domain_name: string;
  inventory_panel_id: string | null;
  panel_email: string | null;
  panel_reseller_id: string | null;
  is_primary: boolean;
  status: ItemStatus;
  users_listed: number | null;
  users_deleted: number;
  users_remaining_b3: number | null;
  groups_listed: number | null;
  groups_deleted: number;
  domain_delete_attempts: number;
  error_code: string | null;
  error_message: string | null;
  started_at: string | null;
  finished_at: string | null;
}

interface RunDetail {
  run: RemovalRun;
  items: RemovalRunItem[];
}

interface PreviewEntry {
  id?: string;
  domain_name: string;
  panel_email?: string | null;
  panel_reseller_id?: string | null;
}

interface PreviewResponse {
  run: RemovalRun;
  willDelete: PreviewEntry[];
  primary: PreviewEntry[];
  notFound: PreviewEntry[];
}

interface InventoryRunSummary {
  id: string;
  status: 'queued' | 'running' | 'completed' | 'failed';
  panels_total: number;
  panels_done: number;
  panels_errored: number;
  domains_total: number;
  started_at: string | null;
  finished_at: string | null;
}

interface InventoryRunDetailResponse {
  run: InventoryRunSummary;
  items: unknown[];
}

interface Toast {
  type: 'success' | 'error';
  message: string;
}

/* ─── Constants ──────────────────────────────────────────── */

const TERMINAL_STATUSES: ReadonlySet<RunStatus> = new Set([
  'completed',
  'failed',
  'cancelled',
]);

const INFLIGHT_STATUSES: ReadonlySet<RunStatus> = new Set([
  'queued',
  'phase_a_running',
  'phase_a_done',
  'awaiting_confirmation',
  'phase_b1_running',
  'phase_b2_waiting',
  'phase_b3_running',
  'phase_b4_running',
  'phase_b5_verifying',
]);

const INITIAL_VISIBLE = 15;
const PAGE_INCREMENT = 40;

/* ─── Helpers ────────────────────────────────────────────── */

function relativeTime(iso: string | null): string {
  if (!iso) return '-';
  const diff = Date.now() - new Date(iso).getTime();
  const sec = Math.floor(diff / 1000);
  if (sec < 5) return 'just now';
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} min ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  return new Date(iso).toLocaleDateString();
}

function durationLabel(start: string | null, end: string | null): string {
  if (!start) return '-';
  const endMs = end ? new Date(end).getTime() : Date.now();
  const ms = endMs - new Date(start).getTime();
  if (ms < 1000) return '<1s';
  const sec = Math.round(ms / 1000);
  if (sec < 60) return `${sec}s`;
  const min = Math.floor(sec / 60);
  const restSec = sec % 60;
  return restSec === 0 ? `${min}m` : `${min}m ${restSec}s`;
}

function statusBadgeClass(status: RunStatus): string {
  // Normalize new phase_* / awaiting_confirmation statuses onto our base palette.
  switch (status) {
    case 'queued':
      return 'cln-badge--queued';
    case 'phase_a_running':
    case 'phase_b1_running':
    case 'phase_b3_running':
    case 'phase_b4_running':
    case 'phase_b5_verifying':
      return 'cln-badge--running';
    case 'phase_a_done':
    case 'awaiting_confirmation':
      return 'cln-badge--honey';
    case 'phase_b2_waiting':
      return 'cln-badge--queued';
    case 'completed':
      return 'cln-badge--completed';
    case 'failed':
      return 'cln-badge--failed';
    case 'cancelled':
      return 'cln-badge--muted';
    default:
      return 'cln-badge--muted';
  }
}

function itemBadgeClass(status: ItemStatus): string {
  switch (status) {
    case 'pending':
      return 'cln-badge--pending';
    case 'not_found':
      return 'cln-badge--muted';
    case 'primary_skipped':
      return 'cln-badge--honey';
    case 'b1_users_killed':
    case 'b3_users_verified':
      return 'cln-badge--running';
    case 'b4_domain_deleted':
    case 'b5_verified_deleted':
      return 'cln-badge--success';
    case 'b4_needs_group_auth':
      return 'cln-badge--partial';
    case 'error':
      return 'cln-badge--error';
    default:
      return 'cln-badge--muted';
  }
}

const PlusIcon = (): JSX.Element => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);

const RefreshIcon = (): JSX.Element => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M23 4v6h-6M1 20v-6h6" />
    <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
  </svg>
);

const ChevronIcon = (): JSX.Element => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="15 18 9 12 15 6" />
  </svg>
);

const DownloadIcon = (): JSX.Element => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="7 10 12 15 17 10" />
    <line x1="12" y1="15" x2="12" y2="3" />
  </svg>
);

/* ─── View modes ─────────────────────────────────────────── */

type View =
  | { mode: 'list' }
  | { mode: 'wizard' }
  | { mode: 'detail'; runId: string };

/* ─── Main component ─────────────────────────────────────── */

export function DomainRemoval(): JSX.Element {
  const [view, setView] = useState<View>({ mode: 'list' });
  const [runs, setRuns] = useState<RemovalRun[] | null>(null);
  const [toast, setToast] = useState<Toast | null>(null);

  const showToast = useCallback((type: Toast['type'], message: string) => {
    setToast({ type, message });
    window.setTimeout(() => setToast(null), 4500);
  }, []);

  const refreshList = useCallback(async (): Promise<void> => {
    try {
      const data = await api<RemovalRun[]>('/api/cleanup/removal/runs');
      setRuns((prev) => {
        if (!prev) return data;
        const headIds = new Set(data.map((r) => r.id));
        const older = prev.filter((r) => !headIds.has(r.id));
        return [...data, ...older];
      });
    } catch (err) {
      showToast('error', err instanceof Error ? err.message : 'Failed to load runs');
    }
  }, [showToast]);

  /* List polling: 3s while inflight, 7s otherwise. */
  useEffect(() => {
    if (view.mode === 'list') void refreshList();
  }, [view.mode, refreshList]);

  const listPollIntervalRef = useRef<number | null>(null);
  useEffect(() => {
    if (view.mode !== 'list') {
      if (listPollIntervalRef.current !== null) {
        window.clearInterval(listPollIntervalRef.current);
        listPollIntervalRef.current = null;
      }
      return;
    }
    const hasInflight = runs?.some((r) => INFLIGHT_STATUSES.has(r.status));
    const intervalMs = hasInflight ? 3000 : 7000;
    if (listPollIntervalRef.current !== null) {
      window.clearInterval(listPollIntervalRef.current);
    }
    listPollIntervalRef.current = window.setInterval(() => {
      void refreshList();
    }, intervalMs);
    return () => {
      if (listPollIntervalRef.current !== null) {
        window.clearInterval(listPollIntervalRef.current);
        listPollIntervalRef.current = null;
      }
    };
  }, [view.mode, runs, refreshList]);

  /* Render: detail / wizard / list */

  if (view.mode === 'detail') {
    return (
      <RunDetailView
        runId={view.runId}
        onBack={() => {
          setView({ mode: 'list' });
          void refreshList();
        }}
        onOpenRun={(runId) => setView({ mode: 'detail', runId })}
        onError={(msg) => showToast('error', msg)}
        toast={toast}
      />
    );
  }

  if (view.mode === 'wizard') {
    return (
      <NewRunWizard
        onCancelToList={() => {
          setView({ mode: 'list' });
          void refreshList();
        }}
        onOpenDetail={(runId) => setView({ mode: 'detail', runId })}
        showToast={showToast}
        toast={toast}
      />
    );
  }

  return (
    <RunsListView
      runs={runs}
      onNew={() => setView({ mode: 'wizard' })}
      onOpen={(runId) => setView({ mode: 'detail', runId })}
      onRefresh={() => void refreshList()}
      toast={toast}
    />
  );
}

/* ─── State 1: List view ─────────────────────────────────── */

function RunsListView({
  runs,
  onNew,
  onOpen,
  onRefresh,
  toast,
}: {
  runs: RemovalRun[] | null;
  onNew: () => void;
  onOpen: (runId: string) => void;
  onRefresh: () => void;
  toast: Toast | null;
}): JSX.Element {
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE);
  const totalRuns = runs?.length ?? 0;
  const visibleRuns = runs ? runs.slice(0, visibleCount) : null;
  const inflight = runs?.find((r) => INFLIGHT_STATUSES.has(r.status)) ?? null;

  return (
    <div className="cln-root">
      <div className="cln-scroll">
        <header className="cln-header">
          <div className="cln-header-row">
            <div>
              <h1 className="cln-title">Domain Removal</h1>
              <p className="cln-subtitle">
                Bulk-delete domains across the inventory pool from a Google Sheets URL.
              </p>
            </div>
            <button
              type="button"
              className="cln-btn cln-btn--primary"
              onClick={onNew}
              disabled={inflight !== null}
              title={inflight ? 'A removal run is already in progress' : 'Start a new removal run'}
            >
              <PlusIcon />
              {inflight ? 'Run in progress...' : 'New Removal Run'}
            </button>
          </div>
        </header>

        {inflight && (
          <section className="cln-card cln-snapshot-card">
            <div className="cln-snapshot-meta">
              An active removal run is in flight ({inflight.status}). Click the row below to view its
              progress.
            </div>
          </section>
        )}

        <section className="cln-card">
          <div className="cln-card-header">
            <h2 className="cln-card-title">
              Runs <span className="cln-cell-faint">({Math.min(visibleCount, totalRuns)} of {totalRuns})</span>
            </h2>
            <button type="button" className="cln-icon-btn" onClick={onRefresh} title="Refresh list">
              <RefreshIcon />
            </button>
          </div>

          {runs === null && <div className="cln-empty">Loading...</div>}

          {runs !== null && runs.length === 0 && (
            <div className="cln-empty">
              <h3 className="cln-empty-title">No runs yet</h3>
              <p className="cln-empty-text">
                Click <strong>New Removal Run</strong> above to start one from a Google Sheets URL.
              </p>
            </div>
          )}

          {visibleRuns !== null && visibleRuns.length > 0 && (
            <table className="cln-table">
              <thead>
                <tr>
                  <th>Run</th>
                  <th>Status</th>
                  <th className="cln-col-num">Domains</th>
                  <th className="cln-col-num">Users killed</th>
                  <th className="cln-col-num">Domains deleted</th>
                  <th>By</th>
                  <th className="cln-col-date">Started</th>
                  <th className="cln-col-date">Duration</th>
                </tr>
              </thead>
              <tbody>
                {visibleRuns.map((r) => (
                  <tr key={r.id} className="cln-row" onClick={() => onOpen(r.id)}>
                    <td className="cln-cell-mono cln-cell-faint">{r.id.slice(0, 8)}</td>
                    <td>
                      <span className={`cln-badge ${statusBadgeClass(r.status)}`}>{r.status}</span>
                      {r.trigger_source === 'auto' && <span className="cln-badge cln-badge--queued" style={{ marginLeft: 6 }}>AUTO</span>}
                      {r.trigger_source === 'rerun' && <span className="cln-badge cln-badge--queued" style={{ marginLeft: 6 }}>RERUN</span>}
                    </td>
                    <td className="cln-col-num cln-num--ink">{r.domains_targeted}</td>
                    <td className="cln-col-num">{r.users_deleted}</td>
                    <td className="cln-col-num">
                      {r.domains_deleted}
                      {r.domains_failed > 0 && (
                        <span className="cln-num-detail"> · {r.domains_failed} err</span>
                      )}
                    </td>
                    <td className="cln-cell-mono cln-cell-faint">{r.triggered_by ?? '-'}</td>
                    <td className="cln-col-date cln-cell-faint">
                      {relativeTime(r.started_at ?? r.created_at)}
                    </td>
                    <td className="cln-col-date cln-cell-faint">
                      {durationLabel(r.started_at, r.finished_at)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {visibleRuns !== null && totalRuns > visibleCount && (
            <div className="cln-loadmore-row">
              <button
                type="button"
                className="cln-btn cln-btn--ghost"
                onClick={() => setVisibleCount((c) => Math.min(c + PAGE_INCREMENT, totalRuns))}
              >
                Load more
              </button>
            </div>
          )}
        </section>
      </div>
      {toast && <div className={`cln-toast cln-toast--${toast.type}`}>{toast.message}</div>}
    </div>
  );
}

/* ─── State 2: New-run wizard ────────────────────────────── */

type WizardStep = 'url' | 'phase_a' | 'preview';

function NewRunWizard({
  onCancelToList,
  onOpenDetail,
  showToast,
  toast,
}: {
  onCancelToList: () => void;
  onOpenDetail: (runId: string) => void;
  showToast: (type: Toast['type'], message: string) => void;
  toast: Toast | null;
}): JSX.Element {
  const [step, setStep] = useState<WizardStep>('url');
  const [sheetUrl, setSheetUrl] = useState('');
  const [starting, setStarting] = useState(false);
  const [runId, setRunId] = useState<string | null>(null);
  const [run, setRun] = useState<RemovalRun | null>(null);
  const [inventoryRun, setInventoryRun] = useState<InventoryRunSummary | null>(null);
  const [preview, setPreview] = useState<PreviewResponse | null>(null);
  const [typedConfirmation, setTypedConfirmation] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  /* Submit URL: POST /removal/runs */
  const handleStart = async (): Promise<void> => {
    const url = sheetUrl.trim();
    if (url.length === 0) {
      showToast('error', 'Paste a Google Sheets URL first.');
      return;
    }
    setStarting(true);
    try {
      const result = await api<{ runId: string; sheetId: string }>(
        '/api/cleanup/removal/runs',
        { method: 'POST', body: JSON.stringify({ sheetUrl: url }) },
      );
      setRunId(result.runId);
      setStep('phase_a');
    } catch (err) {
      if (err instanceof ApiError && err.code === 'RUN_IN_PROGRESS') {
        const existingRunId = (err.data as { existingRunId?: string } | undefined)?.existingRunId;
        if (existingRunId) {
          showToast('success', 'A removal run is already in progress - opening it now.');
          onOpenDetail(existingRunId);
          return;
        }
      }
      const msg =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Failed to start run';
      showToast('error', msg);
    } finally {
      setStarting(false);
    }
  };

  /* Phase A polling: poll the removal run + linked inventory run at 1s. */
  const phaseAPollRef = useRef<number | null>(null);
  useEffect(() => {
    if (step !== 'phase_a' || !runId) {
      if (phaseAPollRef.current !== null) {
        window.clearInterval(phaseAPollRef.current);
        phaseAPollRef.current = null;
      }
      return;
    }

    let stopped = false;

    const poll = async (): Promise<void> => {
      try {
        const detail = await api<RunDetail>(`/api/cleanup/removal/runs/${runId}`);
        if (stopped) return;
        setRun(detail.run);

        if (detail.run.inventory_run_id) {
          try {
            const invDetail = await api<InventoryRunDetailResponse>(
              `/api/cleanup/inventory/runs/${detail.run.inventory_run_id}`,
            );
            if (!stopped) setInventoryRun(invDetail.run);
          } catch {
            // Inventory detail might 404 momentarily during creation race; ignore.
          }
        }

        if (detail.run.status === 'awaiting_confirmation') {
          // Fetch preview and advance.
          try {
            const prev = await api<PreviewResponse>(
              `/api/cleanup/removal/runs/${runId}/preview`,
            );
            if (!stopped) {
              setPreview(prev);
              setStep('preview');
            }
          } catch (err) {
            if (!stopped) {
              showToast('error', err instanceof Error ? err.message : 'Failed to load preview');
            }
          }
          return;
        }

        if (
          detail.run.status === 'failed' ||
          detail.run.status === 'cancelled' ||
          (detail.run.status !== 'queued' &&
            detail.run.status !== 'phase_a_running' &&
            detail.run.status !== 'phase_a_done')
        ) {
          // Either failed/cancelled (jump to detail to surface the error) or
          // somehow advanced past confirmation without our intervention - jump
          // to the detail view in either case.
          if (!stopped) onOpenDetail(runId);
        }
      } catch (err) {
        if (!stopped) {
          showToast('error', err instanceof Error ? err.message : 'Failed to poll run');
        }
      }
    };

    void poll();
    phaseAPollRef.current = window.setInterval(() => {
      void poll();
    }, 1000);

    return () => {
      stopped = true;
      if (phaseAPollRef.current !== null) {
        window.clearInterval(phaseAPollRef.current);
        phaseAPollRef.current = null;
      }
    };
  }, [step, runId, showToast, onOpenDetail]);

  const handleConfirm = async (): Promise<void> => {
    if (!runId || typedConfirmation !== 'DELETE') return;
    setConfirming(true);
    try {
      await api<{ runId: string; shards: number; items: number }>(
        `/api/cleanup/removal/runs/${runId}/confirm`,
        { method: 'POST', body: JSON.stringify({ typedConfirmation: 'DELETE' }) },
      );
      onOpenDetail(runId);
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Failed to confirm run';
      showToast('error', msg);
    } finally {
      setConfirming(false);
    }
  };

  const handleCancel = async (): Promise<void> => {
    if (!runId) {
      onCancelToList();
      return;
    }
    setCancelling(true);
    try {
      await api<{ runId: string; status: 'cancelled' }>(
        `/api/cleanup/removal/runs/${runId}/cancel`,
        { method: 'POST', body: JSON.stringify({}) },
      );
      onCancelToList();
    } catch (err) {
      const msg =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Failed to cancel run';
      showToast('error', msg);
    } finally {
      setCancelling(false);
    }
  };

  /* Phase A progress numbers */
  const phaseAPanelsTotal = inventoryRun?.panels_total ?? 0;
  const phaseAPanelsDone = inventoryRun?.panels_done ?? 0;
  const phaseADomainsTotal = inventoryRun?.domains_total ?? 0;
  const phaseAPct =
    phaseAPanelsTotal > 0
      ? Math.min(100, Math.round((phaseAPanelsDone / phaseAPanelsTotal) * 100))
      : 0;
  const phaseAElapsed = inventoryRun ? durationLabel(inventoryRun.started_at, inventoryRun.finished_at) : '-';

  return (
    <div className="cln-root">
      <div className="cln-scroll">
        <header className="cln-header">
          <button
            type="button"
            className="cln-back"
            onClick={() => {
              if (runId) {
                void handleCancel();
              } else {
                onCancelToList();
              }
            }}
          >
            <ChevronIcon />
            <span>Back to runs</span>
          </button>
          <div style={{ marginTop: 16 }}>
            <h1 className="cln-title">New Removal Run</h1>
            <p className="cln-subtitle">
              Paste a Google Sheets URL with a <code>DOMAINS</code> column under the
              <code> MASTER</code> tab. Phase A pulls a fresh inventory snapshot, then you confirm
              the parsed domain list before B-stages execute.
            </p>
          </div>
        </header>

        <div className="cln-wizard-container">
          {/* Step 1: URL input */}
          <section className="cln-wizard-step">
            <div className="cln-wizard-step-header">Step 1 - Sheet URL</div>
            <div className="cln-wizard-row">
              <input
                type="text"
                className="cln-search-input"
                placeholder="https://docs.google.com/spreadsheets/d/..."
                value={sheetUrl}
                onChange={(e) => setSheetUrl(e.target.value)}
                disabled={step !== 'url' || starting}
                style={{ flex: 1 }}
              />
              <button
                type="button"
                className="cln-btn cln-btn--primary"
                onClick={handleStart}
                disabled={step !== 'url' || starting || sheetUrl.trim().length === 0}
              >
                {starting ? 'Starting...' : 'Start'}
              </button>
            </div>
          </section>

          {/* Step 2: Phase A progress */}
          {(step === 'phase_a' || step === 'preview') && (
            <section className="cln-wizard-step">
              <div className="cln-wizard-step-header">Step 2 - Domain inventory pull (Phase A)</div>
              <div className="cln-stage-row" style={{ gridTemplateColumns: '1fr auto auto auto' }}>
                <div className="cln-stage-progress">
                  <div className="cln-stage-progress-bar" style={{ width: `${phaseAPct}%` }} />
                </div>
                <div className="cln-stage-elapsed">
                  {phaseAPanelsDone}/{phaseAPanelsTotal} panels
                </div>
                <div className="cln-stage-elapsed">
                  {phaseADomainsTotal} domains
                </div>
                <div className="cln-stage-elapsed">{phaseAElapsed}</div>
              </div>
              {step === 'preview' && (
                <div className="cln-stage-name" style={{ marginTop: 8 }}>
                  Phase A complete - ready to preview deletion targets.
                </div>
              )}
              {step === 'phase_a' && run && (
                <div className="cln-stage-name" style={{ marginTop: 8 }}>
                  Run {run.id.slice(0, 8)} - status: {run.status}
                </div>
              )}
            </section>
          )}

          {/* Step 3: Preview + typed confirmation */}
          {step === 'preview' && preview && (
            <section className="cln-wizard-step">
              <div className="cln-wizard-step-header">Step 3 - Preview &amp; confirm</div>

              <PreviewSection
                title={`Will delete (${preview.willDelete.length})`}
                titleClass="cln-preview-list-title--rose"
                entries={preview.willDelete}
                showOwnership
              />

              <PreviewSection
                title={`Primary domain - skipped (${preview.primary.length})`}
                titleClass="cln-preview-list-title--honey"
                entries={preview.primary}
                showOwnership
              />

              <PreviewSection
                title={`Not found in inventory (${preview.notFound.length})`}
                titleClass="cln-preview-list-title--muted"
                entries={preview.notFound}
                showOwnership={false}
              />

              <div className="cln-wizard-row" style={{ marginTop: 24, alignItems: 'center' }}>
                <input
                  type="text"
                  className="cln-confirm-input"
                  placeholder="Type DELETE"
                  value={typedConfirmation}
                  onChange={(e) => setTypedConfirmation(e.target.value)}
                  disabled={confirming || cancelling}
                />
                <button
                  type="button"
                  className="cln-btn cln-btn--primary"
                  onClick={handleConfirm}
                  disabled={
                    typedConfirmation !== 'DELETE' ||
                    confirming ||
                    cancelling ||
                    preview.willDelete.length === 0
                  }
                  title={
                    preview.willDelete.length === 0
                      ? 'Nothing to delete - cancel and try a different sheet'
                      : 'Run the removal'
                  }
                >
                  {confirming ? 'Confirming...' : 'Run Removal'}
                </button>
                <button
                  type="button"
                  className="cln-btn cln-btn--ghost"
                  onClick={handleCancel}
                  disabled={confirming || cancelling}
                >
                  {cancelling ? 'Cancelling...' : 'Cancel'}
                </button>
              </div>
            </section>
          )}
        </div>
      </div>
      {toast && <div className={`cln-toast cln-toast--${toast.type}`}>{toast.message}</div>}
    </div>
  );
}

function PreviewSection({
  title,
  titleClass,
  entries,
  showOwnership,
}: {
  title: string;
  titleClass: string;
  entries: PreviewEntry[];
  showOwnership: boolean;
}): JSX.Element {
  return (
    <div className="cln-preview-list">
      <div className={`cln-preview-list-title ${titleClass}`}>{title}</div>
      {entries.length === 0 ? (
        <div className="cln-cell-faint" style={{ fontSize: 13 }}>(none)</div>
      ) : (
        <table className="cln-table">
          <tbody>
            {entries.map((e, i) => (
              <tr key={`${e.domain_name}-${i}`}>
                <td className="cln-cell-mono">{e.domain_name}</td>
                {showOwnership && (
                  <td className="cln-cell-mono cln-cell-faint">
                    {e.panel_reseller_id ?? '-'}
                    {e.panel_email ? ` (${e.panel_email})` : ''}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

/* ─── State 3: Run detail ────────────────────────────────── */

function RunDetailView({
  runId,
  onBack,
  onOpenRun,
  onError,
  toast,
}: {
  runId: string;
  onBack: () => void;
  onOpenRun: (runId: string) => void;
  onError: (msg: string) => void;
  toast: Toast | null;
}): JSX.Element {
  const [detail, setDetail] = useState<RunDetail | null>(null);

  const refreshDetail = useCallback(async (): Promise<void> => {
    try {
      const data = await api<RunDetail>(`/api/cleanup/removal/runs/${runId}`);
      setDetail(data);
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Failed to load run');
    }
  }, [runId, onError]);

  useEffect(() => {
    void refreshDetail();
  }, [refreshDetail]);

  /* Detail polling: 2.5s until terminal status. */
  const detailPollRef = useRef<number | null>(null);
  useEffect(() => {
    detailPollRef.current = window.setInterval(() => {
      void refreshDetail();
    }, 2500);
    return () => {
      if (detailPollRef.current !== null) {
        window.clearInterval(detailPollRef.current);
        detailPollRef.current = null;
      }
    };
  }, [refreshDetail]);

  useEffect(() => {
    if (!detail) return;
    if (TERMINAL_STATUSES.has(detail.run.status)) {
      if (detailPollRef.current !== null) {
        window.clearInterval(detailPollRef.current);
        detailPollRef.current = null;
      }
    }
  }, [detail]);

  return (
    <div className="cln-root">
      <div className="cln-scroll">
        <header className="cln-header">
          <button type="button" className="cln-back" onClick={onBack}>
            <ChevronIcon />
            <span>Back to runs</span>
          </button>
        </header>
        {detail ? (
          <RunDetailBody
            detail={detail}
            onRefresh={() => void refreshDetail()}
            onOpenRun={onOpenRun}
            onError={onError}
          />
        ) : (
          <div className="cln-loading">Loading run...</div>
        )}
      </div>
      {toast && <div className={`cln-toast cln-toast--${toast.type}`}>{toast.message}</div>}
    </div>
  );
}

function RunDetailBody({
  detail,
  onRefresh,
  onOpenRun,
  onError,
}: {
  detail: RunDetail;
  onRefresh: () => void;
  onOpenRun: (runId: string) => void;
  onError: (msg: string) => void;
}): JSX.Element {
  const { run, items } = detail;
  const [showRerunConfirm, setShowRerunConfirm] = useState(false);
  const [rerunTyped, setRerunTyped] = useState('');
  const [rerunning, setRerunning] = useState(false);

  const isTerminal = TERMINAL_STATUSES.has(run.status);

  const handleRerun = async (): Promise<void> => {
    if (rerunTyped !== 'DELETE') return;
    setRerunning(true);
    try {
      const data = await api<{ runId: string }>(
        `/api/cleanup/removal/runs/${run.id}/rerun`,
        { method: 'POST', body: JSON.stringify({ typedConfirmation: 'DELETE' }) },
      );
      onOpenRun(data.runId);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'RUN_IN_PROGRESS') {
        const existingId = (err.data as { existingRunId?: string } | undefined)?.existingRunId;
        onError('A removal run is already in progress.');
        if (existingId) onOpenRun(existingId);
      } else {
        onError(err instanceof Error ? err.message : 'Failed to rerun');
      }
    } finally {
      setRerunning(false);
      setShowRerunConfirm(false);
      setRerunTyped('');
    }
  };

  // Item sort order: bad rows first (error / needs_group_auth), pending second, good rows last.
  const sortRank = (s: ItemStatus): number => {
    if (s === 'error' || s === 'b4_needs_group_auth') return 0;
    if (s === 'pending' || s === 'b1_users_killed' || s === 'b3_users_verified') return 1;
    return 2;
  };
  const sortedItems = [...items].sort((a, b) => sortRank(a.status) - sortRank(b.status));

  return (
    <>
      <section className="cln-summary-card">
        <div className="cln-summary-top">
          <div>
            <h2 className="cln-summary-title">
              Run <span className="cln-cell-mono cln-cell-faint">{run.id.slice(0, 8)}</span>
            </h2>
            <div className="cln-summary-meta">
              {run.triggered_by ? <>Triggered by <strong>{run.triggered_by}</strong> · </> : null}
              {relativeTime(run.created_at)}
              {run.started_at && <> · ran {durationLabel(run.started_at, run.finished_at)}</>}
              {run.sheet_id && <> · <span className="cln-cell-mono">{run.sheet_id}</span></>}
              {run.rerun_of_run_id && (
                <> · rerun of <span className="cln-cell-mono">{run.rerun_of_run_id.slice(0, 8)}</span></>
              )}
            </div>
          </div>
          <div className="cln-summary-actions">
            <span className={`cln-badge ${statusBadgeClass(run.status)}`}>{run.status}</span>
            {run.status === 'completed' && (
              <a
                className="cln-btn cln-btn--ghost"
                href={`/api/cleanup/removal/runs/${run.id}/csv`}
                download
              >
                <DownloadIcon />
                CSV
              </a>
            )}
            {isTerminal && (
              <button
                type="button"
                className="cln-btn cln-btn--ghost"
                onClick={() => setShowRerunConfirm(true)}
                disabled={rerunning}
                title="Rerun the entire removal again as a new run"
              >
                Rerun
              </button>
            )}
            <button type="button" className="cln-icon-btn" onClick={onRefresh} title="Refresh">
              <RefreshIcon />
            </button>
          </div>
        </div>

        {showRerunConfirm && (
          <div className="cln-wizard-row" style={{ marginTop: 16, alignItems: 'center', gap: 8 }}>
            <span>
              Rerun all {items.length} domains as a new run. Type DELETE to confirm:
            </span>
            <input
              type="text"
              className="cln-confirm-input"
              placeholder="Type DELETE"
              value={rerunTyped}
              onChange={(e) => setRerunTyped(e.target.value)}
              disabled={rerunning}
            />
            <button
              type="button"
              className="cln-btn cln-btn--primary"
              onClick={() => void handleRerun()}
              disabled={rerunTyped !== 'DELETE' || rerunning}
            >
              {rerunning ? 'Starting...' : 'Rerun removal'}
            </button>
            <button
              type="button"
              className="cln-btn cln-btn--ghost"
              onClick={() => {
                setShowRerunConfirm(false);
                setRerunTyped('');
              }}
              disabled={rerunning}
            >
              Cancel
            </button>
          </div>
        )}

        {run.error_message && <div className="cln-summary-error">{run.error_message}</div>}
      </section>

      {/* Per-stage progress strip */}
      <StageStrip run={run} items={items} />

      {/* Summary stat grid */}
      <section className="cln-summary-card">
        <div className="cln-summary-grid">
          <SummaryStat label="Requested" value={run.domains_requested} />
          <SummaryStat label="Targeted" value={run.domains_targeted} variant="ink" />
          <SummaryStat label="Users killed" value={run.users_deleted} variant="sage" />
          <SummaryStat label="Groups deleted" value={run.groups_deleted} variant="sage" />
          <SummaryStat label="Domains deleted" value={run.domains_deleted} variant="sage" />
          <SummaryStat
            label="Failed"
            value={run.domains_failed}
            variant={run.domains_failed > 0 ? 'rose' : 'muted'}
          />
          <SummaryStat
            label="Needs group auth"
            value={run.domains_needs_group_auth}
            variant={run.domains_needs_group_auth > 0 ? 'honey' : 'muted'}
          />
          <SummaryStat
            label="Ghost copies removed"
            value={run.ghost_domains_deleted ?? 0}
            variant={(run.ghost_domains_deleted ?? 0) > 0 ? 'sage' : 'muted'}
          />
          {(run.ghost_domains_failed ?? 0) > 0 && (
            <SummaryStat label="Ghosts left" value={run.ghost_domains_failed} variant="honey" />
          )}
        </div>
      </section>

      <section className="cln-card">
        <div className="cln-card-header">
          <h2 className="cln-card-title">Domain results ({items.length})</h2>
        </div>

        {items.length === 0 && (
          <div className="cln-empty">
            <span>No items in this run yet.</span>
          </div>
        )}

        {items.length > 0 && (
          <table className="cln-table">
            <thead>
              <tr>
                <th>Domain</th>
                <th>Panel</th>
                <th>Status</th>
                <th className="cln-col-num">Users del</th>
                <th className="cln-col-num">Groups del</th>
                <th>Notes / error</th>
              </tr>
            </thead>
            <tbody>
              {sortedItems.map((it) => {
                const isBad = it.status === 'error' || it.status === 'b4_needs_group_auth';
                return (
                  <tr key={it.id} className={isBad ? 'cln-row--bad' : undefined}>
                    <td className="cln-cell-mono">{it.domain_name}</td>
                    <td className="cln-cell-mono cln-cell-faint">
                      {it.panel_reseller_id ?? '-'}
                      {it.panel_email ? ` (${it.panel_email})` : ''}
                    </td>
                    <td>
                      <span className={`cln-badge ${itemBadgeClass(it.status)}`}>{it.status}</span>
                    </td>
                    <td className="cln-col-num">{it.users_deleted}</td>
                    <td className="cln-col-num">{it.groups_deleted}</td>
                    <td className="cln-cell-faint">
                      {it.error_code && <span className="cln-error-tag">{it.error_code}</span>}
                      {it.error_message && <span className="cln-error-msg"> {it.error_message}</span>}
                      {it.status === 'b4_needs_group_auth' && (
                        <div className="cln-cta-resolve">
                          Resolve: authorize <code>admin.directory.group</code> on this panel.
                        </div>
                      )}
                      {it.status === 'error' && it.error_message && (
                        <div className="cln-cta-resolve">{it.error_message}</div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </section>
    </>
  );
}

/* ─── Stage strip helper ─────────────────────────────────── */

type StageState = 'pending' | 'running' | 'completed' | 'failed';

interface StageRow {
  key: 'a' | 'b1' | 'b2' | 'b3' | 'b4' | 'b5';
  name: string;
  state: StageState;
  // Optional progress values (numerator / denominator) shown on the running stage.
  progress?: { done: number; total: number };
  elapsed?: string;
}

function deriveStages(run: RemovalRun, items: RemovalRunItem[]): StageRow[] {
  const status = run.status;

  // Default everything pending.
  const stages: Record<StageRow['key'], StageState> = {
    a: 'pending',
    b1: 'pending',
    b2: 'pending',
    b3: 'pending',
    b4: 'pending',
    b5: 'pending',
  };

  if (status === 'phase_a_running' || status === 'queued') {
    stages.a = 'running';
  } else if (status === 'phase_a_done' || status === 'awaiting_confirmation') {
    stages.a = 'completed';
  } else if (status === 'phase_b1_running') {
    stages.a = 'completed';
    stages.b1 = 'running';
  } else if (status === 'phase_b2_waiting') {
    stages.a = 'completed';
    stages.b1 = 'completed';
    stages.b2 = 'running';
  } else if (status === 'phase_b3_running') {
    stages.a = 'completed';
    stages.b1 = 'completed';
    stages.b2 = 'completed';
    stages.b3 = 'running';
  } else if (status === 'phase_b4_running') {
    stages.a = 'completed';
    stages.b1 = 'completed';
    stages.b2 = 'completed';
    stages.b3 = 'completed';
    stages.b4 = 'running';
  } else if (status === 'phase_b5_verifying') {
    stages.a = 'completed';
    stages.b1 = 'completed';
    stages.b2 = 'completed';
    stages.b3 = 'completed';
    stages.b4 = 'completed';
    stages.b5 = 'running';
  } else if (status === 'completed') {
    stages.a = 'completed';
    stages.b1 = 'completed';
    stages.b2 = 'completed';
    stages.b3 = 'completed';
    stages.b4 = 'completed';
    stages.b5 = 'completed';
  } else if (status === 'failed') {
    // Mark whatever ran and treat the rest as failed/pending.
    if (run.error_message) {
      stages.a = 'failed';
    }
  }
  // 'cancelled' leaves all stages as their defaulted pending - the badge tells the operator.

  // Compute per-stage progress from item counts.
  const total = Math.max(1, run.domains_targeted || items.length || 1);
  const b1Done = items.filter(
    (it) =>
      it.status === 'b1_users_killed' ||
      it.status === 'b3_users_verified' ||
      it.status === 'b4_domain_deleted' ||
      it.status === 'b5_verified_deleted' ||
      it.status === 'b4_needs_group_auth' ||
      it.status === 'error',
  ).length;
  const b3Done = items.filter(
    (it) =>
      it.status === 'b3_users_verified' ||
      it.status === 'b4_domain_deleted' ||
      it.status === 'b5_verified_deleted' ||
      it.status === 'b4_needs_group_auth' ||
      it.status === 'error',
  ).length;
  const b4Done = items.filter(
    (it) =>
      it.status === 'b4_domain_deleted' ||
      it.status === 'b5_verified_deleted' ||
      it.status === 'b4_needs_group_auth' ||
      it.status === 'error',
  ).length;
  const b5Done = items.filter(
    (it) =>
      it.status === 'b5_verified_deleted' ||
      it.status === 'b4_domain_deleted' ||
      it.status === 'b4_needs_group_auth' ||
      it.status === 'error',
  ).length;

  const startedMs = run.started_at ? new Date(run.started_at).getTime() : null;
  const finishedMs = run.finished_at ? new Date(run.finished_at).getTime() : null;
  const elapsedSinceStart = startedMs
    ? durationLabel(run.started_at, finishedMs ? run.finished_at : null)
    : '-';

  return [
    {
      key: 'a',
      name: 'Phase A',
      state: stages.a,
      elapsed: stages.a !== 'pending' ? elapsedSinceStart : undefined,
    },
    {
      key: 'b1',
      name: 'Phase B1',
      state: stages.b1,
      progress: stages.b1 === 'running' ? { done: b1Done, total } : undefined,
    },
    {
      key: 'b2',
      name: 'Phase B2',
      state: stages.b2,
    },
    {
      key: 'b3',
      name: 'Phase B3',
      state: stages.b3,
      progress: stages.b3 === 'running' ? { done: b3Done, total } : undefined,
    },
    {
      key: 'b4',
      name: 'Phase B4',
      state: stages.b4,
      progress: stages.b4 === 'running' ? { done: b4Done, total } : undefined,
    },
    {
      key: 'b5',
      name: 'Phase B5',
      state: stages.b5,
      progress: stages.b5 === 'running' ? { done: b5Done, total } : undefined,
    },
  ];
}

function StageStrip({
  run,
  items,
}: {
  run: RemovalRun;
  items: RemovalRunItem[];
}): JSX.Element {
  const stages = deriveStages(run, items);

  const labels: Record<StageRow['key'], string> = {
    a: 'Inventory pull',
    b1: 'Volume user delete',
    b2: 'Propagation wait',
    b3: 'Re-verify users',
    b4: 'Domain delete',
    b5: 'Final verification',
  };

  return (
    <section className="cln-stage-strip">
      {stages.map((s) => {
        const pct =
          s.progress && s.progress.total > 0
            ? Math.min(100, Math.round((s.progress.done / s.progress.total) * 100))
            : 0;
        return (
          <div key={s.key} className="cln-stage-row">
            <span className="cln-stage-name">
              {s.name} - {labels[s.key]}
            </span>
            <div>
              {s.state === 'running' && s.progress ? (
                <div className="cln-stage-progress">
                  <div className="cln-stage-progress-bar" style={{ width: `${pct}%` }} />
                </div>
              ) : (
                <span style={{ display: 'inline-block', width: 200 }} />
              )}
            </div>
            <span className={`cln-stage-state cln-stage-state--${s.state}`}>
              {s.state === 'running' && s.progress
                ? `${s.progress.done}/${s.progress.total}`
                : s.state}
            </span>
            <span className="cln-stage-elapsed">{s.elapsed ?? ''}</span>
          </div>
        );
      })}
    </section>
  );
}

/* ─── SummaryStat (inline-copied from DomainInventory) ───── */

function SummaryStat({
  label,
  value,
  variant = 'default',
  hint,
}: {
  label: string;
  value: number;
  variant?: 'default' | 'sage' | 'rose' | 'honey' | 'muted' | 'ink';
  hint?: string;
}): JSX.Element {
  return (
    <div className="cln-summary-stat">
      <span className="cln-summary-stat-label">{label}</span>
      <span className={`cln-summary-stat-value cln-summary-stat-value--${variant}`}>{value}</span>
      {hint && <span className="cln-summary-stat-hint">{hint}</span>}
    </div>
  );
}
